import React from 'react';
import styled from 'styled-components';
import { colors } from '@annote/core/styles';
import { SettingMetadata } from '@annote/settings';

const SettingRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid ${colors.lines.link};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:last-child {
    border-bottom: none;
  }
`;

const SettingLabel = styled.div`
  flex: 1;
`;

const SettingTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.5rem;
`;

const SettingDescription = styled.div`
  font-size: 1rem;
  color: ${colors.text.subtitle};
  margin-top: 0.25rem;
`;

const DisabledBadge = styled.span`
  display: inline-block;
  padding: 0.125rem 0.5rem;
  background: ${colors.bg.surface};
  border: 1px solid ${colors.lines.link};
  border-radius: 0.25rem;
  font-size: 0.75rem;
  color: ${colors.text.subtitle};
  margin-left: 0.5rem;
  font-weight: 500;
`;

const SettingControl = styled.div`
  flex-shrink: 0;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 3rem;
  height: 1.5rem;

  input {
    opacity: 0;
    width: 0;
    height: 0;

    &:checked + span {
      background-color: ${colors.text.link};
    }

    &:checked + span:before {
      transform: translateX(1.5rem);
    }
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.2s;
  border-radius: 1.5rem;

  &:before {
    position: absolute;
    content: '';
    height: 1.125rem;
    width: 1.125rem;
    left: 0.1875rem;
    bottom: 0.1875rem;
    background-color: white;
    transition: 0.2s;
    border-radius: 50%;
  }
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${colors.lines.link};
  border-radius: 0.5rem;
  background: ${colors.bg.primary};
  color: ${colors.text.primary};
  font-size: 1rem;
  min-width: 150px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.text.link};
  }
`;

const NumberInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${colors.lines.link};
  border-radius: 0.5rem;
  background: ${colors.bg.primary};
  color: ${colors.text.primary};
  font-size: 1rem;
  width: 80px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${colors.text.link};
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${colors.lines.link};
  border-radius: 0.75rem;
  background: ${colors.bg.primary};
  color: ${colors.text.primary};
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 6rem;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${colors.text.link};
  }
`;

const ColorPickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ColorInput = styled.input`
  width: 3rem;
  height: 2rem;
  border: 1px solid ${colors.lines.link};
  border-radius: 0.5rem;
  cursor: pointer;
`;

const ColorText = styled.span`
  font-size: 0.875rem;
  font-family: monospace;
  color: ${colors.text.subtitle};
`;

const ResetButton = styled.button`
  background: none;
  border: none;
  color: ${colors.text.link};
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    color: ${colors.text.subtitle};
    cursor: default;
    opacity: 0.5;
    text-decoration: none;
  }
`;

const NumberWithUnit = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Unit = styled.span`
  font-size: 1rem;
  color: ${colors.text.subtitle};
`;

interface ProfileSettingFieldProps {
  metadata: SettingMetadata;
  value: unknown;
  onChange: (value: unknown) => void;
}

export const ProfileSettingField: React.FC<ProfileSettingFieldProps> = ({ metadata, value, onChange }) => {
  const renderControl = (): React.ReactNode => {
    switch (metadata.type) {
      case 'boolean':
        return (
          <Toggle>
            <input type="checkbox" checked={value as boolean} onChange={(e): void => onChange(e.target.checked)} />
            <Slider />
          </Toggle>
        );

      case 'number':
        return (
          <NumberWithUnit>
            <NumberInput
              type="number"
              value={value as number}
              onChange={(e): void => onChange(parseFloat(e.target.value))}
              min={metadata.min}
              max={metadata.max}
              step={metadata.step}
            />
            {metadata.unit && <Unit>{metadata.unit}</Unit>}
          </NumberWithUnit>
        );

      case 'select':
        return (
          <Select value={value as string} onChange={(e): void => onChange(e.target.value)}>
            {metadata.options?.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </Select>
        );

      case 'textarea':
        return <TextArea value={value as string} onChange={(e): void => onChange(e.target.value)} rows={4} />;

      case 'color': {
        const isDefaultColor = value === metadata.defaultValue;
        return (
          <ColorPickerWrapper>
            <ColorInput type="color" value={value as string} onChange={(e): void => onChange(e.target.value)} />
            <ColorText>{value as string}</ColorText>
            <ResetButton
              onClick={(): void => onChange(metadata.defaultValue)}
              disabled={isDefaultColor}
              title={isDefaultColor ? 'Already using default color' : 'Reset to default'}
            >
              Reset
            </ResetButton>
          </ColorPickerWrapper>
        );
      }

      default:
        return null;
    }
  };

  return (
    <SettingRow $disabled={metadata.disabled}>
      <SettingLabel>
        <SettingTitle>
          {metadata.label}
          {metadata.disabled && <DisabledBadge>{metadata.disabledReason || 'Disabled'}</DisabledBadge>}
        </SettingTitle>
        {metadata.description && <SettingDescription>{metadata.description}</SettingDescription>}
      </SettingLabel>
      <SettingControl>{renderControl()}</SettingControl>
    </SettingRow>
  );
};
